import { useEffect, useState, useRef } from 'react'
import { createClient } from '@sanity/client'
import Link from 'next/link'
import { toHTML, uriLooksSafe } from '@portabletext/to-html'
import Head from 'next/head'
import Script from 'next/script'
import { useRouter } from 'next/router'
import {
  useWindowSize,
  Footer,
  Header,
  TryEdworking,
  Subheader,
  HubspotChat,
  EdworkingPromotion,
  TopBanner,
} from 'js/components'
import { timeDiff, newLive } from 'js'
import Author from 'components/modules/Author'
import {
  OrganizationSchema,
  WebPageSchema,
  ArticleSchema,
  BreadcrumbListSchema,
  FAQSchema,
} from 'js/schemas'
import Image from 'next/image'
import path from 'path'
import fs from 'fs/promises' // or 'fs' if using callbacks

const myPortableTextComponents = {
  types: {
    image: ({ value }) => {
      return `<img src="${value.url}" alt="${value.alt || ''}" />`
    },
  },
  marks: {
    link: ({ children, value }) => {
      const href = value.href || ''

      if (uriLooksSafe(href)) {
        const rel = href.startsWith('/') ? undefined : 'noreferrer noopener nofollow'
        return `<a href="${href}" rel="${rel}">${children}</a>`
      }

      // If the URI appears unsafe, render the children (eg, text) without the link
      return children
    },
  },
}

export const client = createClient({
  projectId: 'k9mx6lbx', //process.env.SANITY_PROJECT_ID,
  dataset: 'production', //process.env.SANITY_DATASET,
  useCdn: true,
  apiVersion: '2024-01-18', // use current date (YYYY-MM-DD) to target the latest API version
  // token: process.env.SANITY_SECRET_TOKEN // Only if you want to update content with the client
})

export async function getStaticPaths() {
  const queryNews = `
    *[_type == "news" && approved == true]{
      id,
      title,
      language,
      "slug": slug.current,
      "category": category->title,
      publishedAt
    }`
  const newsRelated = await client.fetch(queryNews)
  const array = newsRelated.map((news) => {
    return [
      {
        locale: news.language,
        params: { category: news.category, news: news.slug },
      },
    ]
  })
  return {
    paths: array.flat(1),
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params, locale, locales }) {
  const slug = params.news

  const queryNews = `
    *[_type == "news" && slug.current == $slug][0]{
      id,
      title,
      language,
      "slug": slug.current,
      "category": category->title,
      "sumup": body[0].children[0].text,
      language,
      "article": body[]{
        ...,
        _type == 'image' => {
          "url": asset->url,
          alt,
        }
      },
      "cover": cover.asset->url,
      metaTitle,
      metaDescription,
      "url": slug.current,
      "author": author->{
        "id": _id,
        "firstname": name,
        "lastname": "",
        "description": bio[],
        "profileImg": image.asset->url,
        linkedin
      },
      "createdAt": publishedAt,
      "updatedAt": _updatedAt
    }`
  const newsData = await client.fetch(queryNews, { slug })
  if (newsData) {
    newsData.article = toHTML(newsData.article, {
      components: myPortableTextComponents,
    })
    newsData.author.description = toHTML(newsData.author.description)
    newsData.keywords = newsData.keywords?.join(' ') ?? null
  }
  const querySimilarNews = `
    *[_type == "news" && approved == true && language == $language && slug.current != $currentSlug ] | order(publishedAt desc)[0...8]{
      id,
      title,
      language,
      "slug": slug.current,
      "category": category->title,
      "cover": cover.asset->url,
      "resume": summary,
      "author": author->{
        "firstname": name,
        "lastname": "",
      },
      "createdAt": publishedAt
    }`
  const similar = await client.fetch(querySimilarNews, {
    language: locale,
    category: newsData.category,
    currentSlug: newsData.slug,
  })

  if (!newsData || !newsData) {
    return {
      notFound: true,
    }
  }
  let content
  try {
    const filePath = path.join(process.cwd(), 'public', 'content', 'en', 'news.json')
    const jsonData = await fs.readFile(filePath, 'utf8')
    content = JSON.parse(jsonData)
  } catch (error) {
    console.error('error', error)
  }
  return {
    props: { news: newsData, similar, content, locale, locales, params },
    revalidate: 24 * 60 * 60,
  }
}
const ArticleProgress = () => {
  useEffect(() => {
    window.addEventListener('scroll', onScroll)
  }, [])
  const [scrollTop, setScrollTop] = useState(1)
  const onScroll = (e) => {
    const scroll1 = Math.round(
      ((e.target.documentElement.scrollTop + window.screen.height) /
        e.target.documentElement.scrollHeight) *
        100
    )
    setScrollTop(scroll1)
  }
  return (
    <div
      className="articleprogress"
      style={{
        background: `linear-gradient(90deg, #fcb50480   0%, #fcb50480   ${scrollTop}%, #09323a10 ${scrollTop}%, #09323a10 100%)`,
      }}
    ></div>
  )
}
export default function News({ news, similar, content, locale }) {
  const title = news?.title,
    metaTitle = (news.metaTitle || news.title).replace(/(^\w{1})|(\s+\w{1})/g, (letter) =>
      letter.toUpperCase()
    ),
    author = news?.author?.firstname + ' ' + news?.author?.lastname,
    created = timeDiff(news?.createdAt),
    length = Math.ceil(news?.article?.split(' ').length / 200)

  const [promotion, setPromotion] = useState(false)
  const [slideInBanner, setSlideInBanner] = useState(false)

  const { asPath } = useRouter()

  const onMouseOut = () => {
    const goon = localStorage.getItem('promotion')
    if (!goon) setPromotion(true)
    document.body.removeEventListener('mouseleave', onMouseOut)
  }

  useEffect(() => {
    setTimeout(function () {
      document.body.addEventListener('mouseleave', onMouseOut)
    }, 3000)

    if (relatedInfo !== null) {
      setTimeout(() => setSlideInBanner(true), 5000)
    }
  }, [])

  const optimization = content?.optimization
  function getRelatedInfo(blogTitle) {
    blogTitle = blogTitle.toLowerCase()

    for (const category in optimization) {
      //if (optimization[category].blogs.some(blog => blog.toLowerCase() === blogTitle)) {

      const blogs = optimization[category].blogs.map((title) => title.toLowerCase())
      if (blogs.includes(blogTitle)) {
        return {
          intext: optimization[category].intext,
          slidein: optimization[category].slidein,
        }
      }
    }
    return null
  }
  const relatedInfo = getRelatedInfo(news.title)
  const faqsSection =
    news.article.indexOf('<h2>FAQs</h2>') >= 0
      ? news.article.substring(news.article.indexOf('<h2>FAQs</h2>') + 12 + 1)
      : null
  var h4Tags = new RegExp('<h4>(.*?)</h4>', 'g')
  var pTags = new RegExp('<p>(.*?)</p>', 'g')
  const questions = faqsSection?.match(h4Tags)?.map((q) => q.substring(4, q.length - 5))
  const answers = faqsSection?.match(pTags)?.map((a) => a.substring(3, a.length - 4))
  const listfaqs = questions?.map((q, i) => {
    return { question: q, answer: answers[i] }
  })

  return (
    <>
      <Script
        src="https://www.googletagmanager.com/gtag/js?id=UA-166939348-2"
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            
            gtag('config', 'UA-166939348-2');
        `}
      </Script>
      <Script id="google-tag-manager" strategy="afterInteractive">
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-NRW96HD');
        `}
      </Script>
      <Script id="hotjar" strategy="afterInteractive">
        {`
          (function(h,o,t,j,a,r){
              h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
              h._hjSettings={hjid:2080824,hjsv:6};
              a=o.getElementsByTagName('head')[0];
              r=o.createElement('script');r.async=1;
              r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
              a.appendChild(r);
          })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
        `}
      </Script>
      <OrganizationSchema />
      <WebPageSchema name={metaTitle} description={news.metaDescription} />
      <ArticleSchema
        blog={news}
        url={`https://edworking.com${locale !== 'en' ? `/${locale}` : ''}${asPath}`}
      />
      <BreadcrumbListSchema
        items={[
          {
            name: 'Home',
            url: `https://edworking.com/${locale !== 'en' ? `${locale}/` : ''}`,
          },
          {
            name: 'News',
            url: `https://edworking.com/${locale !== 'en' ? `${locale}/` : ''}news`,
          },
          {
            name: 'Article',
            url: `https://edworking.com${locale !== 'en' ? `/${locale}` : ''}${asPath}`,
          },
        ]}
      />
      <FAQSchema faqs={listfaqs} />
      <Head>
        <title>{metaTitle}</title>

        <meta itemProp="name" content={metaTitle} />
        <meta itemProp="image" content={news.cover} />
        <meta itemProp="description" content={news.metaDescription} />

        <meta name="description" content={news.metaDescription} />

        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={news.metaDescription} />

        <meta property="og:image" content={news.cover} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="3000" />
        <meta property="og:image:height" content="1500" />
        <meta property="og:image" content={news.cover} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="300" />
        <meta property="og:image:height" content="150" />

        <meta property="og:image:secure_url" content={news.cover} />
        <meta
          property="og:url"
          content={`https://edworking.com${locale !== 'en' ? `/${locale}` : ''}/news/${
            news.category
          }/${news.slug}`}
        />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="en_GB" />

        <meta name="msapplication-TileImage" content="http://www.example.com/image01.jpg" />
        <meta
          property="og:site_name"
          content={`${title.replace(/\b\w/g, (l) => l.toUpperCase())}`}
        />
        <link rel="shortcut icon" href="/images/favicon.ico" />

        <meta
          property="og:url"
          content={`https://edworking.com${
            locale !== 'en' ? `/${locale}` : ''
          }/news/${news?.title?.replace(/ /g, '-')}`}
        />
        {/*locales.map((locale1, index) => <link key={index} rel="alternate" hrefLang={locale1} href={`https://edworking.com${locale1 !== 'en' ? `/${locale1}` : ''}/campaign`} /> )*/}
        <link
          rel="alternate"
          hrefLang={locale}
          href={`https://edworking.com${locale !== 'en' ? `/${locale}` : ''}/news/${
            news.category
          }/${news.slug}`}
        />
        <link
          rel="alternate"
          href={`https://edworking.com${locale !== 'en' ? `/${locale}` : ''}/news/${
            news.category
          }/${news.slug}`}
          hrefLang="x-default"
        />
        <link
          rel="canonical"
          href={`https://edworking.com${locale !== 'en' ? `/${locale}` : ''}/news/${
            news.category
          }/${news.slug}`}
          key="canonical"
        />
      </Head>
      <noscript
        dangerouslySetInnerHTML={{
          __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-NRW96HD" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
        }}
      />
      <link itemProp="thumbnailUrl" href={news.cover} />
      <span itemProp="thumbnail" itemScope itemType="http://schema.org/ImageObject">
        <link
          itemProp="url"
          href={`https://edworking.com/news/${news?.category}/${news?.slug}`}
        />
      </span>
      <EdworkingPromotion promotion={promotion} content={content.exitbanner} />
      {/*<TopBanner content={content.topbanner} />*/}
      <Header content={content.header} topbanner={true} />
      <ArticleProgress />
      <Subheader
        type={'blog'}
        title={news.title}
        author={author}
        created={created}
        length={length}
      />
      <Image
        priority
        width={1200}
        height={300}
        style={{
          width: '90%',
          objectFit: 'cover',
          background: 'white',
        }}
        src={news.cover ?? 'https://storage.googleapis.com/dratini/EdworkingDefault.png'}
        className={`coverarticle${news.cover ? '' : ' default'}`}
        alt="article cover"
      />
      <Article content={content.banner} blog={news} relatedInfo={relatedInfo} />
      <Author author={news.author} />
      {/*<Share title={title} link={`https://edworking.com/blog/${blog?.category?.toLowerCase()}/${blog?.title?.replace(/ /g, '-')}`} />*/}
      <SimilarArticles similar={similar} />
      <TryEdworking content={content.endbanner} />
      <Footer content={content.footer} />
      {slideInBanner ? (
        <SlideInBanner
          slideInBanner={slideInBanner}
          relatedInfo={relatedInfo}
          blog={news}
          content={content.banner}
        />
      ) : !relatedInfo ? (
        <StickyBanner blog={news} content={content.banner} />
      ) : null}
      <HubspotChat />
      <div id="livenoti" className="livenoti"></div>
    </>
  )
}
const Banner = ({ blog }) => {
  const cta = 'Get Started Now',
    headline = 'All your work in one place',
    subheadline = 'All-in-one platform for your team and your work. Register now for Free.'
  return (
    <section className="banner">
      <Image
        width="150"
        height="150"
        className="image"
        src="https://storage.googleapis.com/dratini/Edworking%20Logo.png"
        alt=""
      />
      <div className="text">
        <div className="headline">{blog.bannerheadline ? blog.bannerheadline : headline}</div>
        <div className="subheadline">
          {blog.bannersubheadline ? blog.bannersubheadline : subheadline}
        </div>
      </div>
      <a className="blueButton" href="https://app.edworking.com/signup/user/">
        {blog.bannercta ? blog.bannercta : cta}
      </a>
    </section>
  )
}

const VideoBlog = ({ blog, content }) => {
  const [time, setTime] = useState(0)
  const [movedTime, setMovedTime] = useState(0)
  const [speakers, setSpeakers] = useState()
  const [transcript, setTranscript] = useState()
  const [speaker, setSpeaker] = useState()
  const [menu, setMenu] = useState('summaary')
  const size = useWindowSize()
  const articleRef = useRef()

  const refSpeakers = useRef()
  const refMovedTime = useRef()
  const refTime = useRef()
  const refVideo = useRef()
  const refTranscript = useRef()

  var timeTag = new RegExp('[[][0-9][0-9]:[0-9][0-9]:[0-9][0-9][.][0-9][0-9][0-9]]?', 'g')

  refSpeakers.current = speakers
  refMovedTime.current = movedTime
  refTime.current = time
  refTranscript.current = transcript
  useEffect(() => {
    const sec = () => {
      if (!refSpeakers.current) setTimeout(() => sec(), 1000)
      else {
        setTime(refVideo.current?.currentTime)
        const index =
          refSpeakers.current
            .map((speaker) => {
              return (
                parseInt(speaker.innerText.substring(1, 3)) * 60 * 60 +
                parseInt(speaker.innerText.substring(4, 6)) * 60 +
                parseFloat(speaker.innerText.substring(7, 13))
              )
            })
            .findIndex((s) => s > refVideo.current?.currentTime) - 1
        refTranscript.current.map(
          (t, i) => (t.style.color = index === i ? '#0ac9ef' : '#09323a')
        )
        setSpeaker(index)
        setMovedTime(refTime.current)
        const scroll = articleRef.current
        if (scroll)
          scroll.scrollTo({
            behavior: 'smooth',
            top: refSpeakers.current[index]?.offsetTop,
          })
        refMovedTime.current !== refTime.current && refSpeakers.current[index]?.scrollTo //scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
        setTimeout(() => sec(), 500)
      }
    }
    sec()
  }, [])

  useEffect(() => {
    if (!articleRef?.current) return
    const speak = Array.from(articleRef.current?.querySelectorAll('strong'))
    setTranscript(
      Array.from(articleRef.current?.querySelectorAll('p')).filter(
        (a) => a.childNodes[0]?.localName !== 'strong' && a.childNodes[0]?.localName !== 'br'
      )
    )
    setSpeakers(speak)
  }, [blog, size.width])

  if (size.width < 1000) {
    return (
      <section className="videoblog">
        <div className="videosection">
          <div className="videoleft">
            <VideoPlayer src={blog.url} refVideo={refVideo} />
            <div className="title">{blog.rawTitle}</div>
            <div className="selector">
              <span id="selector" className={`top ${menu}`}></span>
              <div id="monthly" className="tab selected" onClick={() => setMenu('summaary')}>
                <span>Summary</span>
              </div>
              <div id="yearly" className="tab" onClick={() => setMenu('transcript')}>
                <span>Transcription</span>
              </div>
            </div>
          </div>
        </div>
        <div className="summary">
          {menu === 'transcript' ? (
            <div
              ref={articleRef}
              className="transcription"
              dangerouslySetInnerHTML={{ __html: blog.article }}
            />
          ) : (
            <span className="resume">{blog.sumup}</span>
          )}
        </div>
        <Banner content={content} blog={blog} />
      </section>
    )
  }
  //.replace(timeTag, (time) => { const h = parseInt(time.substring(1, 3));const m = parseInt(time.substring(4, 6));const s = parseInt(time.substring(7, 11));return (h > 0 ? h + 'h ' : '') + m + 'm ' + s + 's' })
  return (
    <section className="videoblog">
      <div className="videosection">
        <div className="videoleft">
          <VideoPlayer src={blog.url} refVideo={refVideo} />
          <div
            className="title"
            style={{
              maxWidth: refVideo.current?.offsetWidth ? refVideo.current?.offsetWidth : '100%',
            }}
          >
            {blog.rawTitle}
          </div>
        </div>
        <div
          ref={articleRef}
          className="transcription"
          dangerouslySetInnerHTML={{ __html: blog.article }}
        />
      </div>
      <div className="summary">
        <div className="title">Summary</div>
        <span className="resume">{blog.sumup}</span>
      </div>
      <Banner blog={blog} />
    </section>
  )
}
function pad(d) {
  return d < 10 ? '0' + d.toString() : d.toString()
}
export const SimilarArticles = ({ similar, title }) => {
  const router = useRouter()
  return (
    <section className="similararticles nobanner">
      <div className="sectiontop">
        <span className="title">{title ? title : 'Trendy News'}</span>
        <Link href={'/blog'} className="more">
          See All Articles
        </Link>
      </div>
      <div className="articles">
        {similar
          ?.filter((a) => a.cover)
          .map((article, index) => {
            const author = article?.author?.firstname + ' ' + article?.author?.lastname,
              created = timeDiff(article?.createdAt)
            return (
              <Link
                key={index}
                className="single"
                href={`/news/${article.category}/${article.slug}`}
              >
                <Image
                  width={1000}
                  height={500}
                  className="cover"
                  src={article.cover}
                  alt="Cover"
                />
                <span className="title">{article.title}</span>
                <span className="resume">{article.resume}</span>
                <div className="subtitle">
                  <span className="author">
                    BY <span>{author}</span>
                  </span>
                  <span className="created">{created}</span>
                </div>
              </Link>
            )
          })}
      </div>
      <Script
        type="text/javascript"
        id="hs-script-loader"
        async
        defer
        src="//js-eu1.hs-scripts.com/25992597.js"
      ></Script>
    </section>
  )
}

const VideoPlayer = ({ src, refVideo }) => {
  const [volume, setVolume] = useState(0.8)
  const current = `${pad(
    ((Math.round(refVideo.current?.currentTime) % 3600) -
      (Math.round(refVideo.current?.currentTime) % 60)) /
      60
  )}:${pad(Math.round(refVideo.current?.currentTime) % 60)}`
  const duration = `${pad(
    ((Math.round(refVideo.current?.duration) % 3600) -
      (Math.round(refVideo.current?.duration) % 60)) /
      60
  )}:${pad(Math.round(refVideo.current?.duration) % 60)}`
  const refNavigation = useRef()
  const refBar = useRef()
  const refVolume = useRef()

  useEffect(() => {
    if (volume) refVideo.current.volume = volume
  }, [volume])

  return (
    <div className="videoPlayer">
      <video
        onClick={() =>
          refVideo.current?.paused ? refVideo.current.play() : refVideo.current.pause()
        }
        style={{ cursor: 'pointer' }}
        controls={false}
        ref={refVideo}
        src={src}
        alt="Cover"
      />
      {refVideo.current?.paused && (
        <Image
          className="play"
          onClick={() => refVideo.current.play()}
          width={80}
          height={66}
          src={'/images/Play2.svg'}
          alt="Pause"
        />
      )}
      <div className="controlsContainer">
        <div
          className="button"
          onClick={() =>
            refVideo.current?.paused ? refVideo.current.play() : refVideo.current.pause()
          }
        >
          <Image
            className="pause"
            width={9}
            height={10}
            src={refVideo.current?.paused ? '/images/Play.svg' : '/images/Pause.svg'}
            alt="Pause"
          />
        </div>
        <div className="navigation" ref={refNavigation}>
          <span>{current}</span>
          <div
            ref={refBar}
            className="progress"
            onClick={(e) =>
              (refVideo.current.currentTime =
                (refVideo.current?.duration *
                  Math.round(e.clientX - refBar.current.getBoundingClientRect().left)) /
                refBar.current.offsetWidth)
            }
            style={{
              background: `linear-gradient(90deg, #FFFFFFb3 0%, #FFFFFFb3 ${Math.round(
                (refVideo.current?.currentTime / refVideo.current?.duration) * 100
              )}%, #FFFFFF1a ${Math.round(
                (refVideo.current?.currentTime / refVideo.current?.duration) * 100
              )}%, #FFFFFF1a 100%)`,
            }}
          ></div>
          <span>{duration}</span>
          <div
            ref={refVolume}
            className="volume"
            onClick={(e) =>
              setVolume(
                (
                  (e.clientX - refVolume.current.getBoundingClientRect().left) /
                  refVolume.current.offsetWidth
                ).toFixed(2)
              )
            }
            style={{
              background: `linear-gradient(90deg, #FFFFFFb3 0%, #FFFFFFb3 ${
                volume * 100
              }%, #FFFFFF1a ${volume * 100}%, #FFFFFF1a 100%)`,
            }}
          ></div>
        </div>
        <div className="button">
          <Image
            className="expand"
            onClick={() => refVideo.current.requestFullscreen()}
            width={16}
            height={15}
            src="/images/Expand.svg"
            alt="Expand"
          />
        </div>
      </div>
    </div>
  )
}

const Share = ({ link, title }) => {
  const text = `Check%20out%20this%20article%3A%20${title
    .replace(/\b\w/g, (l) => l.toUpperCase())
    .replace(/ /g, '%20')}!`
  return (
    <div className="sharing">
      <a
        target="_blank"
        rel="noreferrer"
        href={`https://twitter.com/intent/tweet?text=${text}&url=${link}`}
      >
        <Image width={40} height={40} src="/images/Twitter.svg" alt="Twitter" />
      </a>
      <a
        target="_blank"
        rel="noreferrer"
        href={`https://www.facebook.com/sharer/sharer.php?u=${link}&quote=${text}`}
      >
        <Image width={40} height={40} src="/images/Facebook.svg" alt="Facebook" />
      </a>
      <a
        target="_blank"
        rel="noreferrer"
        href={`https://t.me/share/url?url=${text}&text=${text}`}
      >
        <Image width={40} height={40} src="/images/Telegram.svg" alt="Telegram" />
      </a>
      <a target="_blank" rel="noreferrer" href={`https://wa.me/?text=${text}%20${link}`}>
        <Image width={40} height={40} src="/images/Whatsapp.svg" alt="Whatsapp" />
      </a>
      <a
        target="_blank"
        rel="noreferrer"
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${link}`}
      >
        <Image width={40} height={40} src="/images/Linkedin.svg" alt="Linkedin" />
      </a>
    </div>
  )
}

const StickyBanner = ({ blog, content }) => {
  const [close, setClose] = useState(false)
  const cta = 'Get Started Now',
    headline = 'All your work in one place',
    subheadline = 'All-in-one platform for your team and your work. Registrer now for Free.'
  return (
    <section className="banner sticky" style={{ display: close ? 'none' : 'flex' }}>
      <div className="close" onClick={() => setClose(true)}>
        <Image width={22} height={22} src="/images/ClosePromotion.png" alt="Close popup" />
      </div>
      <Image
        className="image"
        width={150}
        height={150}
        src="https://storage.googleapis.com/dratini/Edworking%20Logo.png"
        alt=""
      />
      <div className="text">
        <div className="headline">
          {blog.bannerheadline ? blog.bannerheadline : content.headline}
        </div>
        <div className="subheadline">
          {blog.bannersubheadline ? blog.bannersubheadline : content.subheadline}
        </div>
      </div>
      <a className="blueButton" href="https://app.edworking.com/signup/user/">
        {blog.bannercta ? blog.bannercta : content.cta}
      </a>
    </section>
  )
}

const SlideInBanner = ({ relatedInfo, blog, content, slideInBanner }) => {
  const [close, setClose] = useState(false)

  return (
    <section
      className={`banner sticky slide${slideInBanner ? ' up' : ''}`}
      style={{ display: close ? 'none' : 'flex' }}
    >
      <div className="close" onClick={() => setClose(true)}>
        <Image width={22} height={22} src="/images/ClosePromotion.png" alt="Close popup" />
      </div>
      <Image
        className="image"
        width={150}
        height={150}
        src="https://storage.googleapis.com/dratini/Edworking%20Logo.png"
        alt=""
      />
      <div className="text">
        <div className="headline">{relatedInfo?.slidein.headline}</div>
        <div className="subheadline">{relatedInfo?.slidein.subheadline}</div>
      </div>
      <a className="blueButton" href={`${relatedInfo?.slidein.url2}`}>
        {relatedInfo?.slidein.cta2}
      </a>
    </section>
  )
}

export const Article = ({ relatedInfo, blog, content }) => {
  const edBanner = `
    <section class="banner">
      <Image class="image" src="https://storage.googleapis.com/dratini/Edworking%20Logo.png" alt="" />
      <div class="text">
        <div class="headline">${
          relatedInfo
            ? relatedInfo.intext.headline
            : blog.bannerheadline
              ? blog.bannerheadline
              : content.headline
        }</div>
        <div class="subheadline">${
          relatedInfo
            ? relatedInfo.intext.subheadline
            : blog.bannersubheadline
              ? blog.bannersubheadline
              : content.subheadline
        }</div>
      </div>
      <a class="blueButton" href=${
        relatedInfo ? relatedInfo.intext.url1 : 'https://app.edworking.com/signup/user/'
      }>${
        relatedInfo ? relatedInfo.intext.cta1 : blog.bannercta ? blog.bannercta : content.cta
      }</a>
    </section>
  `
  var re = new RegExp(' ' + blog.keywords?.split(' ').join(' | ') + ' ', 'gi')
  var aTag = new RegExp('<a[^>]*>', 'gi')
  var hTag = new RegExp('<h[2-4]', 'g')
  var onlyText = new RegExp('[^a-zA-Z0-9-]', 'g')
  const spaceText = new RegExp('/ /g', 'g')
  const article = blog.article
  var blogEdworking = new RegExp('blog.edworking.com', 'g')
  var linkEdworking = new RegExp('href="www.edworking.com"', 'g')
  var httpEdworking = new RegExp('http://', 'g')
  const initBold = [...article?.matchAll(re)]
  const step = Math.ceil(initBold.length / 5)
  const bolds = initBold.reduce((bs, b, index) => {
    return index % step === 0 ? bs.concat(b.index) : bs
  }, [])
  const button1 = article.indexOf('<h', Math.round(article.length / 3))
  const button2 = article.indexOf('<h', Math.round((2 * article.length) / 3))
  const banner = article.indexOf('<h', Math.round(article.length / 2))
  var alltags = new RegExp('<h2(.*?)>(.*?)</h2>', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  var faqs = new RegExp('<h2>FAQs</h2>', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  var allfaqs = new RegExp('<h2>FAQs</h2>?(.*)', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  var h4Tags = new RegExp('<h4>', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  var pTags = new RegExp('<p>', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  const faqsExist = article.match(faqs)
  var spantags = new RegExp(
    '<[/span][span][/strong][strong][/em][em][/u][u][/a][a](.*?)>',
    'g'
  ) //new RegExp('<[h,p,a, ul][2-4]?',"g")
  var h2tags = new RegExp('<[/h2][h2](.*?)>', 'g') //new RegExp('<[h,p,a, ul][2-4]?',"g")
  const [width, setWidth] = useState()
  const articleRef = useRef()
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setWidth(window?.innerWidth)
      window.newLive = newLive
    }
  }, [])
  //.replace(hTag, (matched, index, a, b) => { if(index === button1 || index === button2) return `<a href="https://app.edworking.com/signup/user?promotion=27" class="blueButton">Try Edworking for free</a>${matched}`; else return matched  } )
  return (
    <section ref={articleRef} className="blogarticle">
      {width && width > 500 && (
        <Index article={article} articleRef={articleRef} language={article.language} />
      )}
      <div className="article blog">
        <div
          className="body"
          dangerouslySetInnerHTML={{
            __html:
              article
                .replace(faqs, (matched) => {
                  return matched + '<div class="faqsbox">'
                })
                .replace(allfaqs, (matched) => {
                  return matched.replace(h4Tags, (mat) => {
                    return `<h4 onclick="let yo = this;do{yo = yo?.nextSibling;if(!yo){return}; yo?.classList.toggle('show');}while(yo?.nextSibling?.tagName !== 'H4');">`
                  })
                })
                .replace(blogEdworking, () => {
                  return 'edworking.com/blog'
                })
                .replace(linkEdworking, () => {
                  return 'href="https://edworking.com"'
                })
                .replace(httpEdworking, () => {
                  return 'https://'
                })
                ?.replace(hTag, (matched, index, a, b) => {
                  if (index === button1 || index === button2) return edBanner + matched
                  else return matched
                })
                .replace(re, (matched, index, a, b) => {
                  return bolds.includes(index) ? `<b>${matched}</b>` : matched
                })
                .replace(aTag, (matched, index, a, b) => {
                  return matched.includes('edworking.com')
                    ? matched.replace('noreferrer noopener nofollow', 'follow')
                    : matched.replace('noopener noreferrer', 'noopener noreferrer nofollow')
                })
                .replace(alltags, (match) => {
                  return (
                    `<h2 id="${match
                      .replace(spantags, '')
                      .replace(h2tags, '')
                      .split(' ')
                      .join('-')
                      .replace(onlyText, '')
                      .toLowerCase()}"><img onclick="navigator.clipboard.writeText('https://edworking.com/blog/${
                      blog.category
                    }/${blog.title}#${match
                      .replace(spantags, '')
                      .replace(h2tags, '')
                      .split(' ')
                      .join('-')
                      .replace(onlyText, '')
                      .toLowerCase()}');window.newLive('/images/Logo.svg', 'Url ', 'copied to clipboard')" class="link" src="/images/link.png" />` +
                    match.replace(spantags, '').replace(h2tags, '') +
                    '</h2>'
                  )
                }) + (faqsExist ? '</div>' : ''),
          }}
        />
      </div>
    </section>
  )
}

export const Index = ({ articleRef, language, article }) => {
  const [clicked, setClicked] = useState('')
  const [scrolled, setScrolled] = useState('')
  const [titles, setTitles] = useState([])
  var content = Array.from(titles)
  var onlyText = new RegExp('[^a-zA-Z0-9-]', 'g')
  const router = useRouter()
  function onClick(c) {
    c.scrollIntoView({ block: 'start', behavior: 'smooth' })
    setClicked(c.innerText)
    setTimeout(() => {
      setClicked('')
    }, 1200)
  }

  useEffect(() => {
    setTitles(articleRef.current.querySelectorAll('h2, h3'))
    window.location.hash?.substring(1) &&
      document.getElementById(window.location.hash.substring(1))?.scrollIntoView()
  }, [article, articleRef])

  window.onscroll = () => {
    content?.map((c) => {
      const offset = c.getBoundingClientRect().top // - c.offsetTop
      if (offset < 200) setScrolled(c.innerText)
      return offset
    })
  }

  const title = content?.map((c, index) => {
    const selected = clicked === c.innerText || (clicked === '' && scrolled === c.innerText)
    return (
      <li key={index} className="clickable" onClick={() => onClick(c)}>
        <a
          className={`element${selected ? ' selected' : ''}`}
          href={`#${c.innerText.replace(/ /g, '-').replace(onlyText, '').toLowerCase()}`}
          style={{
            color:
              clicked === c.innerText
                ? '#16ccf0'
                : clicked === '' && scrolled === c.innerText
                  ? '#16ccf0'
                  : '#09323A',
          }}
        >
          {c.innerText}
        </a>
      </li>
    )
  })
  return (
    <div className="content" style={{ marginTop: 30 }}>
      {/*   <div className="title">
        {language === 'es'
          ? 'En este Blog'
          : language === 'it'
            ? 'In questo blog'
            : language === 'fr'
              ? 'Dans ce blog'
              : language === 'pt'
                ? 'Neste blog'
                : language === 'de'
                  ? 'In diesem Blog'
                  : 'In this Blog'}
      </div>*/}
      <ol className="elements">{title}</ol>
    </div>
  )
}

const Organization = ({ author }) => {
  const [linkedin, setLinkdin] = useState(false)
  return (
    <section className="resumewriter">
      <Image
        className="profile"
        width={200}
        height={200}
        src={author.profileImg}
        alt="article cover"
      />
      <div className="body">
        <span className="name">
          About the Organizer: {author.firstname} {author.lastname}{' '}
          {author.linkedin && (
            <a href={author.linkedin} target="_blank" rel="noreferrer">
              <Image
                width={15}
                height={15}
                onMouseOver={() => setLinkdin(true)}
                onMouseLeave={() => setLinkdin(false)}
                className={linkedin ? 'dark' : 'light'}
                src={linkedin ? '/images/inw.png' : '/images/inb.png'}
                alt="Linkedin"
              />
            </a>
          )}
        </span>
        <span className="description">{author.description}</span>
      </div>
    </section>
  )
}
